import React, { memo } from "react";
import PropTypes from "prop-types";
import { TextField } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getIn } from "formik";
import isValid from "date-fns/isValid";

import { InfoTitle } from "../../InfoTitle";
import { ErrorMessage, ContexualHelp } from "../../Notifications";
import { areEqualFieldProps } from "../utils";
import { StyledFormControl, StyledDateTimePicker } from "./styled";
import { formatDateTimeLocal } from "../../../utils/dateTimeHelper";
import { ReactComponent as CalenderIcon } from "../../../assets/icons/calendar.svg";

const DateTimePickerBase = ({
  field,
  label,
  disabled,
  infoTitle,
  format = "dd/MM/yyyy hh:mm a",
  minDate = new Date(1900, 0, 1),
  maxDate,
  disableFuture,
  form: { errors, touched, setFieldValue },
  dateTimePickerProps = {},
  contextualHelpText,
  contextualHelpProps = {},
  inputAdornmentPosition = "end",
  onChange,
  storeAsLocalString = false
}) => {
  const errorMessage = getIn(errors, field.name);
  const isError = errorMessage && getIn(touched, field.name);

  maxDate = maxDate === undefined || maxDate ? maxDate : new Date();

  const currentValue = field.value ? new Date(field.value) : null;

  return (
    <ContexualHelp title={contextualHelpText} {...contextualHelpProps}>
      <StyledFormControl className="formControl datePickerWithEndAdorment">
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <StyledDateTimePicker
            className={"dateTimeMUIInput"}
            autoOk
            autoComplete="off"
            label={label}
            inputFormat={format}
            onChange={value => {
              let storedValue = value;
              if (storeAsLocalString && value instanceof Date && isValid(value)) {
                storedValue = formatDateTimeLocal(value);
              }
              setFieldValue(field.name, storedValue, true);
              if (onChange) {
                onChange(storedValue);
              }
            }}
            disableFuture={disableFuture}
            minDate={minDate || new Date(1900, 0, 1)}
            maxDate={maxDate}
            disabled={disabled}
            value={currentValue}
            defaultCalendarMonth={
              currentValue && isValid(new Date(currentValue)) ? currentValue : new Date()
            }
            InputAdornmentProps={{
              position: inputAdornmentPosition,
              disablePointerEvents: false
            }}
            PaperProps={{
              classes: {
                root: "MuiDatePicker-paper"
              }
            }}
            components={{
              OpenPickerIcon: CalenderIcon
            }}
            {...dateTimePickerProps}
            renderInput={props => (
              <TextField variant="outlined" {...props} name={field.name} error={isError} />
            )}
          />
        </LocalizationProvider>
        {infoTitle && <InfoTitle title={infoTitle} />}
        {isError && <ErrorMessage error={errorMessage} />}
      </StyledFormControl>
    </ContexualHelp>
  );
};

DateTimePickerBase.propTypes = {
  field: PropTypes.object.isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  infoTitle: PropTypes.string,
  format: PropTypes.string,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  disableFuture: PropTypes.bool,
  form: PropTypes.shape({
    errors: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired,
    setFieldValue: PropTypes.func.isRequired
  }).isRequired,
  inputAdornmentPosition: PropTypes.string,
  dateTimePickerProps: PropTypes.object,
  contextualHelpText: PropTypes.string,
  contextualHelpProps: PropTypes.object,
  onChange: PropTypes.func,
  storeAsLocalString: PropTypes.bool
};

export const DateTimePicker = memo(DateTimePickerBase, areEqualFieldProps);
